/**
 * HTML mail, filtered, using the browser's own parser.
 *
 * THIS REPLACES A HAND-ROLLED TOKENISER, AND THE REASON IS WORTH RECORDING
 * because the first version looked fine and passed twenty-three tests.
 *
 * Five adversarial agents executed real payloads against it and confirmed
 * twenty-nine defects. Almost all of them were the same defect wearing
 * different hats — a hand-written scanner disagreeing with an HTML parser —
 * and the failure mode was not "some markup got through", it was MAIL
 * DISAPPEARING:
 *
 *   - `<html><head><meta charset="utf-8"><body>…` — an omitted `</head>`, which
 *     is legal and ubiquitous — rendered as absolutely nothing.
 *   - `<form>` wrapping the body, which every survey mail does: nothing.
 *   - `<input>` or `<embed>`, which are VOID and can never have a close tag, so
 *     the scan for one ran to the end of the document: everything after gone.
 *   - `<div title=don't>` — one apostrophe in an unquoted attribute opened a
 *     quoted region that never closed: rest of the message gone.
 *   - `</style foo>` and `<!-->` and `--!>`, all of which a browser accepts and
 *     the scanner did not: rest of the message gone.
 *   - Every `&nbsp;` and `&mdash;` double-escaped into visible source text, and
 *     `?a=1&amp;b=2` in an href corrupted into a parameter called `amp;b`.
 *   - `'<div>'.repeat(174762)` — inside the 2 MiB body cap — took **37 seconds**
 *     of a single-threaded origin, because closing a tag did `lastIndexOf` over
 *     an open-element stack.
 *
 * Every one of those is free here, because `DOMParser` IS the browser's parser:
 * there is no differential left to have. `parseFromString(html, 'text/html')`
 * produces an INERT document by specification — no script runs, no resource
 * loads, no navigation — so parsing hostile mail costs nothing and the walk
 * below only has to decide what to keep.
 *
 * IT MOVED TO THE CLIENT for the same reason. It was on the origin so there
 * would be one implementation and it could be tested without a DOM; both are
 * still true here — jsdom is already a devDependency and the tests use it — and
 * the client is where a real parser exists for free. The origin now hands over
 * the raw HTML as a JSON STRING, which is the part that was always load-bearing:
 * a browser parses that as data, and the only thing that can turn it into markup
 * is this file, immediately before it goes into a frame that runs nothing.
 *
 * AND IT IS STILL NOT THE SECURITY BOUNDARY. The frame is — no `allow-scripts`,
 * no `allow-same-origin`, `default-src 'none'`. What follows is a PRIVACY and
 * LAYOUT filter, which is what `docs/next.md` asked for once the frame existed.
 */

/** Elements kept as themselves. Anything else is unwrapped or dropped. */
const ALLOWED = new Set([
  'a', 'abbr', 'address', 'article', 'aside', 'b', 'bdi', 'bdo', 'big', 'blockquote', 'br',
  'caption', 'center', 'cite', 'code', 'col', 'colgroup', 'dd', 'del', 'details', 'dfn', 'div',
  'dl', 'dt', 'em', 'figcaption', 'figure', 'font', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'header', 'hr', 'i', 'img', 'ins', 'kbd', 'li', 'main', 'mark', 'nav', 'ol', 'p', 'pre', 'q',
  's', 'samp', 'section', 'small', 'span', 'strike', 'strong', 'sub', 'summary', 'sup', 'table',
  'tbody', 'td', 'tfoot', 'th', 'thead', 'time', 'tr', 'tt', 'u', 'ul', 'var', 'wbr',
])

/**
 * Elements dropped WITH their subtree.
 *
 * THE LIST IS SHORT ON PURPOSE, and the difference from the old one is the
 * whole content-loss fix. `form`, `head`, `input` and `button` are NOT here any
 * more: a `<form>` wraps the body of every survey mail and dropping its subtree
 * threw the message away. They are unwrapped instead — the tag goes, the words
 * stay — which is what a reader wants and what the old version got wrong.
 *
 * What remains is the set whose CONTENT is not prose: script and style bodies
 * are code, `title` is document metadata, `noscript` is the fallback for a
 * client that ran the script we did not, and `template` is inert by definition.
 * `svg`/`math` go because their sub-languages have their own attribute rules
 * (`xlink:href` fetches) and an allowlist written for HTML does not cover them.
 */
const DROP_SUBTREE = new Set(['script', 'style', 'title', 'noscript', 'template', 'svg', 'math', 'object', 'embed', 'applet', 'iframe', 'frame', 'frameset', 'base', 'link', 'meta'])

const GLOBAL_ATTRS = new Set(['title', 'dir', 'lang', 'align', 'valign'])

const ATTRS: Readonly<Record<string, ReadonlySet<string>>> = {
  a: new Set(['href', 'name']),
  img: new Set(['src', 'alt', 'width', 'height']),
  td: new Set(['colspan', 'rowspan', 'width', 'height', 'bgcolor']),
  th: new Set(['colspan', 'rowspan', 'width', 'height', 'bgcolor']),
  table: new Set(['width', 'cellpadding', 'cellspacing', 'border', 'bgcolor']),
  tr: new Set(['bgcolor']),
  col: new Set(['span', 'width']),
  colgroup: new Set(['span', 'width']),
  ol: new Set(['start', 'type']),
  font: new Set(['color', 'face', 'size']),
  time: new Set(['datetime']),
}

/**
 * CSS properties a message may set.
 *
 * `display` IS HERE, and it was a real bug that it was not: mail hides its
 * preheader with `display: none`, so leaving it out made every newsletter open
 * with the invisible one-line summary its designer wrote for the inbox list.
 * The shorthands `background` and `font` are here for the same reason — their
 * longhands were allowed while the shorthand every ESP actually emits was not,
 * so mail lost its backgrounds.
 *
 * What stays out is anything that can move an element out of the flow —
 * `position`, `z-index`, `transform`, `float` is borderline but harmless inside
 * the frame — and anything that can compute or fetch, which `safeCss` handles
 * by value rather than by name.
 */
const CSS_PROPERTIES = new Set([
  'color', 'background', 'background-color', 'background-position', 'background-repeat', 'background-size',
  'font', 'font-size', 'font-weight', 'font-style', 'font-family', 'font-variant',
  'text-align', 'text-decoration', 'text-transform', 'text-indent', 'line-height', 'letter-spacing',
  'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
  'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
  'border', 'border-top', 'border-bottom', 'border-left', 'border-right',
  'border-color', 'border-width', 'border-style', 'border-radius', 'border-collapse', 'border-spacing',
  'width', 'min-width', 'max-width', 'height', 'min-height', 'max-height',
  'display', 'vertical-align', 'white-space', 'list-style', 'list-style-type', 'opacity',
])

/**
 * Can this declaration's VALUE reach the network, or compute?
 *
 * BY VALUE, NOT BY PROPERTY NAME, because the property allowlist is not where
 * this can be decided: an attacker found `list-style-type: symbols(image-set(
 * "http://tracker.example/p.png" 1x))` — an allowed property, no `url(` token
 * anywhere, and a live remote fetch. So every function that can name a resource
 * is refused, and so is a bare scheme or a protocol-relative `//`.
 *
 * `\` is refused because a CSS escape is another spelling of all of the above,
 * and a `/*` is refused because an unterminated comment swallows the rest of
 * the declaration list — the old version passed `color:red/*; font-size:30px`
 * through with the comment intact.
 */
const CSS_UNSAFE = /url\s*\(|image-set|cross-fade|element\s*\(|paint\s*\(|expression\s*\(|@import|https?:|\/\/|\\|\/\*/i

/** Schemes an `href` may use. */
const SAFE_HREF = /^(https?|mailto|tel):/i

export interface SanitiseOptions {
  /**
   * The bytes of one `cid:` part as a `data:` URL, or null when this message
   * has no such part — or has one whose bytes have not arrived yet.
   */
  readonly resolveCid?: (contentId: string) => string | null
  /**
   * The bytes of one remote image as a `data:` URL, or null.
   *
   * A RESOLVER AND NOT A BOOLEAN, and the boolean it replaces was half a
   * feature. `remoteImages: true` kept the sender's `https://…` URL verbatim,
   * which the frame's own `default-src 'none'` then refused — so "Show images"
   * changed this filter's answer, changed no policy, and loaded nothing. Worse,
   * it silently unmounted the banner that explained why, leaving blank boxes.
   *
   * THIS FILTER NOW EMITS NO THIRD-PARTY URL, EVER. The only src it will write
   * is bytes already in hand, so whoever fetched them made a deliberate choice
   * somewhere this file can see — and the frame that holds the output needs no
   * permission to reach anybody.
   */
  readonly resolveRemote?: (url: string) => string | null
}

export interface SanitisedHtml {
  readonly html: string
  /** Remote images kept out. Non-zero is what "show images" offers to undo. */
  readonly blockedRemoteImages: number
  /** `cid:` references matching no part of this message. */
  readonly unresolvedCids: number
  /**
   * Every distinct `http(s)` image URL this message asked for, whether or not
   * it was shown.
   *
   * THE CALLER CANNOT FETCH WHAT IT CANNOT NAME. `blockedRemoteImages` is a
   * count, and a count is enough to offer "Show images" and nothing else; the
   * pane needs the URLs themselves to hand them to the proxy. Reported even
   * when they resolved, so the list is stable across a re-filter — a pane that
   * re-runs this on every arrival would otherwise watch its own dependency
   * shrink under it.
   */
  readonly remoteImageUrls: readonly string[]
}

/**
 * Strip the leading control characters browsers historically ignored inside a
 * scheme, so `java\tscript:` and `\0javascript:` are seen for what they are.
 */
const bareScheme = (value: string): string =>
  // eslint-disable-next-line no-control-regex
  value.replace(/[\x00-\x20]/g, '')

function safeHref(raw: string): string | null {
  const value = raw.trim()
  if (value === '') return null
  // A fragment is kept: an in-message table of contents is real content, and
  // inside an opaque-origin document it can only ever scroll this frame.
  if (value.startsWith('#')) return value
  return SAFE_HREF.test(bareScheme(value)) ? value : null
}

function safeCss(style: string): string {
  const kept: string[] = []
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':')
    if (colon < 0) continue
    const property = declaration.slice(0, colon).trim().toLowerCase()
    const value = declaration.slice(colon + 1).trim()
    if (value === '' || !CSS_PROPERTIES.has(property)) continue
    if (CSS_UNSAFE.test(value)) continue
    kept.push(`${property}: ${value}`)
  }
  return kept.join('; ')
}

/**
 * Rewrite one message's HTML into a fragment the frame can hold.
 *
 * `DOMParser` gives an inert document; this walks it and builds a second,
 * clean one. Building rather than mutating in place is what makes the output
 * an allowlist result rather than a denylist result: an attribute nobody
 * thought about is not carried over, because nothing is carried over except by
 * name.
 */
/**
 * Roughly how deeply this markup nests, without building anything.
 *
 * A running balance over open and close tags: cheap, O(n), and wrong in the
 * ways HTML is always wrong (a stray `</div>` makes it under-count, an unclosed
 * `<p>` makes it over-count). Precision is not the point — the point is telling
 * an ordinary newsletter, which nests tens deep, from a generator that has
 * emitted fifty thousand nested divs.
 */
function approximateDepth(html: string): number {
  let depth = 0
  let deepest = 0
  const tag = /<(\/?)([a-zA-Z][^\s/>]*)/g
  let match: RegExpExecArray | null
  while ((match = tag.exec(html)) !== null) {
    // Void elements never nest, and counting them would make a table of images
    // look deep. The list is short because it only has to cover what mail uses.
    if (/^(br|hr|img|input|meta|link|col|wbr|source|area|base|embed)$/i.test(match[2]!)) continue
    if (match[1] === '/') depth = Math.max(0, depth - 1)
    else deepest = Math.max(deepest, ++depth)
  }
  return deepest
}

/**
 * WHAT A BROWSER ITSELF DOES. Chrome caps HTML nesting at 512 levels and
 * flattens beyond it; jsdom instead recurses until the stack goes. Refusing
 * here rather than discovering it in the parser means the answer does not
 * depend on which of those the reader is using — and it costs one linear scan
 * instead of ten seconds of a parser that is going to fail anyway.
 */
const MAX_DEPTH = 512

export function sanitiseHtml(html: string, options: SanitiseOptions = {}): SanitisedHtml {
  if (approximateDepth(html) > MAX_DEPTH) {
    return { html: '', blockedRemoteImages: 0, unresolvedCids: 0, remoteImageUrls: [] }
  }
  try {
    return filter(html, options)
  } catch {
    // TOTAL, BECAUSE THE ALTERNATIVE IS A MESSAGE THAT WILL NOT OPEN.
    //
    // Deeply nested markup can exhaust the parser: `'<div>'.repeat(10000)` — a
    // shape a table-based newsletter with a generator bug really can reach —
    // throws `Maximum call stack size exceeded` inside the PARSER, before this
    // file's own walk (which is iterative for the same reason) sees anything.
    // Browsers flatten past a few hundred levels and jsdom does not, so the
    // limit differs by environment and cannot be relied on either way.
    //
    // Returning empty rather than rethrowing means the reading pane falls back
    // to the text rendering it already has on screen, which is a slightly worse
    // view of a message rather than no message and an error.
    return { html: '', blockedRemoteImages: 0, unresolvedCids: 0, remoteImageUrls: [] }
  }
}

function filter(html: string, options: SanitiseOptions): SanitisedHtml {
  const resolveCid = options.resolveCid ?? (() => null)
  // DEFAULTS TO "NOTHING FETCHED", which is what a caller that has not thought
  // about it should get: no resolver, no remote image, no request.
  const resolveRemote = options.resolveRemote ?? (() => null)

  const source = new DOMParser().parseFromString(html, 'text/html')
  const out = document.implementation.createHTMLDocument('')
  const root = out.createElement('div')

  let blockedRemoteImages = 0
  let unresolvedCids = 0
  const remoteImageUrls = new Set<string>()

  /**
   * ITERATIVE, NOT RECURSIVE, and that is not a style preference.
   *
   * The first version recursed once per element and `'<div>'.repeat(20000)` —
   * comfortably inside the 2 MiB body cap, and a shape real mail reaches through
   * nested tables — overflowed the call stack. A `RangeError` thrown out of the
   * reading pane is a message that will not open, with no way for the reader to
   * tell why. An explicit stack has no such limit.
   */
  const copyInto = (rootFrom: Node, rootInto: Node): void => {
    const stack: Array<{ from: Node; into: Node }> = [{ from: rootFrom, into: rootInto }]
    while (stack.length > 0) {
      const frame = stack.pop()!
      walk(frame.from, frame.into, stack)
    }
  }

  const walk = (from: Node, into: Node, stack: Array<{ from: Node; into: Node }>): void => {
    for (const node of Array.from(from.childNodes)) {
      if (node.nodeType === 3 /* text */) {
        // THE ENTITY FIX, AND IT IS FREE. The parser has already decoded
        // `&nbsp;` to U+00A0; creating a text node and letting the serialiser
        // re-encode it produces exactly one level of escaping. The old version
        // re-escaped the `&` of an already-formed entity, so every message
        // showed `&nbsp;` as literal text and every `?a=1&amp;b=2` href became
        // a parameter called `amp;b`.
        into.appendChild(out.createTextNode(node.nodeValue ?? ''))
        continue
      }
      if (node.nodeType !== 1 /* element */) continue

      const element = node as Element
      const tag = element.tagName.toLowerCase()

      if (DROP_SUBTREE.has(tag)) continue

      if (!ALLOWED.has(tag)) {
        // UNWRAPPED, NOT DROPPED. `<form>`, `<html>`, `<body>`, `<o:p>` and
        // every custom element a marketing tool invents hold real words; the
        // old version dropped `<form>`'s whole subtree and rendered survey mail
        // as an empty pane.
        stack.push({ from: element, into })
        continue
      }

      if (tag === 'img') {
        const replacement = imageFor(
          element,
          out,
          { cid: resolveCid, remote: resolveRemote, saw: (url) => remoteImageUrls.add(url) },
          (kind) => {
            if (kind === 'remote') blockedRemoteImages++
            else unresolvedCids++
          },
        )
        if (replacement) into.appendChild(replacement)
        continue
      }

      const copy = out.createElement(tag)
      const allowedHere = ATTRS[tag]
      for (const attribute of Array.from(element.attributes)) {
        const name = attribute.name.toLowerCase()
        // Handlers first and unconditionally: depth behind the frame, and the
        // first thing any reviewer looks for.
        if (name.startsWith('on')) continue
        if (name === 'style') {
          const filtered = safeCss(attribute.value)
          if (filtered) copy.setAttribute('style', filtered)
          continue
        }
        if (!GLOBAL_ATTRS.has(name) && !allowedHere?.has(name)) continue
        if (tag === 'a' && name === 'href') {
          const href = safeHref(attribute.value)
          if (href) copy.setAttribute('href', href)
          continue
        }
        copy.setAttribute(name, attribute.value)
      }

      if (tag === 'a') {
        // A link in an opaque-origin document has nowhere to navigate to, so it
        // must open out. `noreferrer` because the address of the message being
        // read is not the sender's business.
        copy.setAttribute('target', '_blank')
        copy.setAttribute('rel', 'noopener noreferrer nofollow')
      }

      into.appendChild(copy)
      stack.push({ from: element, into: copy })
    }
  }

  copyInto(source.body, root)
  return { html: root.innerHTML, blockedRemoteImages, unresolvedCids, remoteImageUrls: [...remoteImageUrls] }
}

/**
 * What a resolver is allowed to hand back: bytes, and nothing else.
 *
 * THE ONE IMAGE PATH THAT ONCE HAD NO GATE. An attacker demonstrated that
 * whatever `resolveCid` returned went straight into `src`, `javascript:`
 * included. It is our own code on the other side, and it is checked anyway.
 *
 * A SAME-ORIGIN PATH IS NO LONGER ACCEPTED, and that is the fix rather than a
 * tightening for its own sake: the frame has an opaque origin and no cookie jar
 * of ours, so a `/_hx/v1/...` src is a request it either cannot make or makes
 * unauthenticated. Bytes are the only src that means the same thing inside that
 * frame as it does out here.
 *
 * SVG IS NAMED AND REFUSED, rather than left to the base64 requirement. That
 * requirement only blocks the UNENCODED form: `[a-z0-9.+-]+` happily matches
 * `svg+xml`, so `data:image/svg+xml;base64,…` passed a gate whose comment
 * claimed it did not. An `<img>`-loaded SVG runs no script and the frame has no
 * `allow-scripts` either, so nothing is reachable today — but three predicates
 * decide whether an image may load here (this one, the Worker's type allowlist,
 * and mailgw's `SERVABLE_TYPES`), and the one documented as narrowest was the
 * widest of the three. They agree now.
 */
const RESOLVED_IMAGE = /^data:image\/(?!svg)[a-z0-9.+-]+;base64,[A-Za-z0-9+/=]*$/i

/**
 * What an `<img>` becomes.
 *
 * Four outcomes, and the two failure ones keep the ALT TEXT rather than
 * deleting the element. An unresolved inline image whose alt says
 * "Q3 revenue chart" is a sentence the reader wants; the old version dropped
 * it and left nothing at all.
 */
function imageFor(
  element: Element,
  out: Document,
  resolve: {
    readonly cid: (id: string) => string | null
    readonly remote: (url: string) => string | null
    /** Told about every third-party URL the message wants, kept or not. */
    readonly saw: (url: string) => void
  },
  count: (kind: 'remote' | 'cid') => void,
): Node | null {
  const raw = (element.getAttribute('src') ?? '').trim()
  const alt = element.getAttribute('alt') ?? ''

  const keep = (src: string): Element => {
    const image = out.createElement('img')
    image.setAttribute('src', src)
    if (alt) image.setAttribute('alt', alt)
    for (const dimension of ['width', 'height']) {
      const value = element.getAttribute(dimension)
      if (value) image.setAttribute(dimension, value)
    }
    const style = safeCss(element.getAttribute('style') ?? '')
    if (style) image.setAttribute('style', style)
    return image
  }

  // A description is better than a gap; an empty alt means the sender said the
  // image is decorative, and honouring that means rendering nothing.
  const described = (): Node | null => {
    if (!alt) return null
    const span = out.createElement('span')
    span.className = 'hearth-image-alt'
    span.textContent = alt
    return span
  }

  if (raw === '') return described()

  // WITHOUT THE CONTROL CHARACTERS, and this is the string that gets fetched as
  // well as the string that gets classified. Mail folds long URLs across lines,
  // and a browser resolving a `src` ignores that whitespace — so the address a
  // proxy is asked for has to be the same one the sender meant, not the one
  // with a newline in the middle.
  const address = bareScheme(raw)
  const scheme = address.toLowerCase()

  if (scheme.startsWith('cid:')) {
    // The Content-ID as the sender wrote it, minus the scheme. Percent-decoded
    // because `cid:logo%40example.com` is how a URL-escaping tool spells an
    // ordinary Content-ID, and matching it byte-for-byte failed to resolve it.
    const token = raw.replace(/^\s*cid:/i, '')
    let id = token
    try {
      id = decodeURIComponent(token)
    } catch {
      // A stray `%` is not an encoding; use it as written.
    }
    const resolved = resolve.cid(id)
    // THE RESOLVER'S ANSWER IS CHECKED, not trusted. See `RESOLVED_IMAGE`.
    if (resolved && RESOLVED_IMAGE.test(resolved)) return keep(resolved)
    count('cid')
    return described()
  }

  // A `data:` image is INLINE — it is bytes the message already carries, it
  // reaches no network, and it is not a tracking pixel. The old version counted
  // it as a blocked remote image, which made "show images" offer to load
  // something that was already there.
  if (scheme.startsWith('data:image/')) return keep(raw)

  if (/^https?:/.test(scheme)) {
    // REPORTED WHETHER OR NOT IT IS SHOWN. The pane fetches from this list, so
    // dropping the ones already in hand would make the list shrink as they
    // arrive — and the effect that reads it re-fire, for ever.
    resolve.saw(address)
    const resolved = resolve.remote(address)
    // The sender's own URL is NEVER what goes in `src`, however loudly the
    // reader asked. Either somebody fetched the bytes for them, or the image
    // does not appear: see `SanitiseOptions.resolveRemote`.
    if (resolved && RESOLVED_IMAGE.test(resolved)) return keep(resolved)
    count('remote')
    return described()
  }

  // Protocol-relative, relative, or a scheme nobody has heard of.
  count('remote')
  return described()
}
